import React, {useEffect, useState, Fragment, forwardRef} from 'react'
import {
	Card,
	Button,
	UncontrolledTooltip,
	PaginationLink,
	Pagination,
} from 'reactstrap'
import DetailFilter from './DetailFilter'
import DimensionRuleRow from './DimensionRuleRow'
import {prefix, extractData} from '../../../utils/index_tagging_tool'
import {api} from '../../../utils/index'
import {faCog, faGlobe, faSort, faTimes} from '@fortawesome/fontawesome-free-solid'
import {FixedSizeList as List} from 'react-window'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {OperationsManager} from './DetailSeoOperations/OperationsManager'
import {LanguageShow} from './DetailSeoLanguage/LanguageShow'
import {LanguageName} from './DetailSeoLanguage/LanguageName'
import {LanguageSearch} from './DetailSeoLanguage/LanguageSearch'
import BulkExcelUpload from '../DetailUtility/BulkExcelUpload'
import ExcelDownload from "../DetailUtility/ExcelDownload";
import Loading from "../../components/Loading";
import RegexpSeoHelper from "../DetailUtility/RegexpSeoHelper";
import {getCustomDimension} from "../../api_methods";
import {DimensionSelector} from "./DimensionFilter";
import {PageNavigator} from "./DimensionDetailPagination";

const TableContext = React.createContext()
const ratio = {
	regex: '300px',
	tag: '200px',
	dependency: '150px',
	priority: '100px',
	checkbox: '100px'
}

// Componente padre della riga dei nomi delle colonne, a cui si aggiunge eventualmente una lista di blocchi delle lingue
function TableNames() {
	const {
		currentOrder,
		handleOrder,
		otherDimensions,
		dimensionsFilter,
		showLanguages,
		handleOtherDimensions,
	} = React.useContext(TableContext)

	return (
		<>
			<div style={{width: ratio.priority}} id='priority-title' className="flex">
				<h3>
					PR{' '}
					<FontAwesomeIcon
						className={
							currentOrder.field === 'priority'
								? 'selected-order'
								: 'not-selected-order'
						}
						style={{cursor: 'pointer'}}
						icon={faSort}
						onClick={() => handleOrder('priority')}
					/>
				</h3>
				<UncontrolledTooltip target='priority-title' placement='top'>
					Priorità delle regole
				</UncontrolledTooltip>
			</div>
      {showLanguages && (
        <div style={{ width: ratio.tag }}>
          <h5>
            TAG EN{' '}
            <FontAwesomeIcon
              style={{ cursor: 'pointer' }}
              icon={faGlobe}
            />
          </h5>
        </div>
      )}
			<div style={{width: ratio.tag}} className="text-left">
				<h3>
					TAG
					<FontAwesomeIcon
						className={
							currentOrder.field === 'rule.tag_it'
								? 'selected-order'
								: 'not-selected-order'
						}
						style={{cursor: 'pointer'}}
						icon={faSort}
						onClick={() => handleOrder('rule.tag_it')}
					/>
				</h3>
			</div>
			<div style={{width: ratio.regex}} className="text-left" id="contains-title">
				<h3>CONTIENE</h3>
				<UncontrolledTooltip target='contains-title' placement='top'>
					Se le regex è verificata, il tag viene applicato
				</UncontrolledTooltip>
			</div>
			<div style={{width: ratio.regex}} className="text-left" id="not-contains-title">
				<h3>NON CONTIENE</h3>
				<UncontrolledTooltip target='not-contains-title' placement='top'>
					Se le regex è verificata, il tag <u>non</u> viene applicato
				</UncontrolledTooltip>
			</div>
			<div style={{width: ratio.dependency}} className="text-left" id="dependency-title">
				<DimensionSelector
					otherDimensions={otherDimensions}
					dimensionsFilter={dimensionsFilter}
					onToggle={handleOtherDimensions}
				/>
				<UncontrolledTooltip target='dependency-title' placement='top'>
					Dimensioni con regole dipendenti
				</UncontrolledTooltip>
			</div>
		</>
	)
}

// Componente padre della riga dei textbox per la ricerca, a cui si aggiunge eventualmente una lista di blocchi delle lingue
function TableSearch() {
	const {
		valueFilter,
		setValueFilter,
		contieneFilter,
		setContieneFilter,
		noncontieneFilter,
		setNoncontieneFilter,
		otherDimensionsFiltered,
		setShouldSearch,
	} = React.useContext(TableContext)

	const handleKeyDown = (e) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			setShouldSearch(true);
		}
	}

	return (
		<>
			<LanguageSearch context={TableContext}/>
			<div style={{width: ratio.tag}}>
				<DetailFilter filter={valueFilter} setFilter={setValueFilter} handleKeyDown={handleKeyDown}/>
			</div>
			<div style={{width: ratio.regex}}>
				<DetailFilter filter={contieneFilter} setFilter={setContieneFilter} handleKeyDown={handleKeyDown}/>
			</div>
			<div style={{width: ratio.regex}}>
				<DetailFilter
					filter={noncontieneFilter}
					setFilter={setNoncontieneFilter}
					handleKeyDown={handleKeyDown}
				/>
			</div>
			{otherDimensionsFiltered.map((d) => (
				<div className="w-seo-control-field text-center" style={{width: ratio.dependency}}>{d.display_name}</div>
			))}
		</>
	)
}

// copiato da documentazione di react table
const innerElementType = forwardRef(({style, ...rest}, ref) => {
	return (
		<Fragment>
			<div
				ref={ref}
				style={{
					...style,
					heigth: `${parseFloat(style.height)}px`,
				}}
				{...rest}
			/>
		</Fragment>
	)
})

// Macrocomponente che comprende la tabella seo per la modifica delle regole, e i vari bottoni per il salvataggio/aggiunta regole
export default function DimensionDetailForm({
	ruleStateManager,
	loader,
	detailId,
	detailLinkedId,
	detailReport,
	detailType,
	otherDimensions,
	dependencyDimensions,
	dependencyDimensionsTags,
	updateDependenciesDimensions,
	listRef,
}) {
	// STATE VARIABLES

	// Paginazione, che può essere presente quando ci sono più di X regole manual che arrivano da backend (> X rompono google chrome intasando completamente la RAM)
	const [page, setPage] = useState(1)
	const [lastFetched, setLastFetched] = useState(1)
	// Filtri per regole
	const [valueFilter, setValueFilter] = useState('')
	const [contieneFilter, setContieneFilter] = useState('')
	const [noncontieneFilter, setNoncontieneFilter] = useState('')
	// Mantiene le regole disabilitate (aka non modificabili e non applicate in fase di run/simula)
	const [disabledRules, setDisabledRules] = useState([])
	const [thanosRules, setThanosRules] = useState([])
	// Variabili e funzione per ordinare le colonne, in ordine alfabetico crescente/decrescente (o numerico per la priority). Per ora disponibile solo per PR e NOME
	const [currentOrder, setCurrentOrder] = useState({field: 'priority', order: 'asc'})
	// Checkbox delle regole
	const [checked, setChecked] = useState([])
	// Lingue
	const [showLanguages, setShowLanguages] = useState(false)
	const [selectedLanguages, setSelectedLanguages] = useState(['Inglese'])
	const [shouldSearch, setShouldSearch] = useState(false);

	// OTHER VARIABLES
	const languageOptions = ['Inglese', 'Italiano']
	const otherDimensionsFiltered = otherDimensions.filter(
		(d) =>
			dependencyDimensions.includes(d.display_name) ||
			dependencyDimensions.includes(d.display_name.split(' '))
	)
	// Mostro regole che rispettano il filtro (se c'è), + le regole nuove non ancora salvate
	const manualRules = ruleStateManager.rules.filter(
		(rule) => rule.id === -1 || ((contieneFilter === '' || rule.pos_regex) && (noncontieneFilter === '' || rule.neg_regex))
	)

	// Calcola la larghezza della tabella in base alle lingue (se mostrate) e altre dimensioni selezionate
	const listWidth =
		1400 +
		Math.max(otherDimensionsFiltered.length - 2, 0) * 150 + // le prime due dimensioni sono già comprese nei 1300
		(showLanguages ? selectedLanguages.length * 200 : 0)


	// FUNCTIONS
	const handleOrder = (field) => {
		if (currentOrder.field === field) {
			currentOrder.order = currentOrder.order === 'asc' ? 'desc' : 'asc'
		} else {
			currentOrder.field = field
			currentOrder.order = 'asc'
		}
		ruleStateManager.reorder(currentOrder)
		setCurrentOrder(currentOrder)
	}

	// Recupera le regole disabilitate da backend
	const getDisabledThanos = () => {
		api.get(prefix + `disabled-rules/?custom=${detailId}`)
			.then(extractData)
			.then((data) => setDisabledRules(data.map((dr) => dr.disabled)))

		api.get(prefix + `thanos-rules/?custom=${detailId}`)
			.then(extractData)
			.then((data) => setThanosRules(data.map((th) => th.thanos)))
	}

	// Funzioni di comodo per riempire/svuotare le checkbox delle regole
	const fillChecked = () => {
		setChecked(ruleStateManager.rules.map((r) => r.id).filter((id) => id !== -1))
	}

	const emptyChecked = () => {
		setChecked([])
	}

	// Paginazione
	useEffect(() => {
		if (valueFilter !== '' || contieneFilter !== '' || noncontieneFilter !== '') {
			// Cerca tra le regole via API solo se ci sono almeno 3 lettere
			let queryParams = {}

			queryParams['query'] = valueFilter

			queryParams['queryContiene'] = contieneFilter

			queryParams['queryNonContiene'] = noncontieneFilter

			if (Object.keys(queryParams).length > 0 && shouldSearch) {
				ruleStateManager.fetch(1, queryParams)
				setLastFetched(-1)
				setShouldSearch(false);
			}
		} else {
			// Evita che vengano ricaricate le regole se per qualche motivo si riseleziona la stessa pagina
			if (lastFetched !== page) {
				ruleStateManager.fetch(page)
				setLastFetched(page)
				setShouldSearch(false);
			}
		}
	}, [shouldSearch, page, valueFilter, contieneFilter, noncontieneFilter])

	useEffect(() => {
		// Al primo caricamento setto l'ordinamento di default
		ruleStateManager.reorder(currentOrder)
		// E recupero la lista di regole disabilitate di questa dimensione (se è custom)
		detailType === 'custom-dimensions' && getDisabledThanos()
	}, [])

	return (
		<>
			<Card className="border-1 bg-secondary border-top-0">
				<TableContext.Provider
					value={{
						valueFilter: valueFilter,
						setValueFilter: setValueFilter,
						contieneFilter: contieneFilter,
						setContieneFilter: setContieneFilter,
						noncontieneFilter: noncontieneFilter,
						setNoncontieneFilter: setNoncontieneFilter,
						otherDimensions: otherDimensions,
						otherDimensionsFiltered: otherDimensionsFiltered,
						currentOrder: currentOrder,
						handleOrder: handleOrder,
						dimensionsFilter: dependencyDimensions,
						handleOtherDimensions: updateDependenciesDimensions,
						disabledRules: disabledRules,
						setDisabledRules: setDisabledRules,
						thanosRules: thanosRules,
						setThanosRules: setThanosRules,
						checked: checked,
						setChecked: setChecked,
						fillChecked: fillChecked,
						emptyChecked: emptyChecked,
						rules: ruleStateManager.rules,
						removeRule: ruleStateManager.remove,
						dimension: detailId,
						dimensionType: detailType,
						linked: detailLinkedId,
						showLanguages: showLanguages,
						setShowLanguages: setShowLanguages,
						selectedLanguages: selectedLanguages,
						setSelectedLanguages: setSelectedLanguages,
						languageOptions: languageOptions,
						setShouldSearch: setShouldSearch,
					}}
				>
					{ruleStateManager.pages > 1 && (
						<div className={'mb-3'}>
							<PageNavigator pages={ruleStateManager.pages} setPage={setPage} active={page}/>
						</div>
					)}
					<div style={{position: 'relative'}}>
						{/* riferimento per popup lingue - fuori dall'overflow */}
						<div style={{overflowX: 'auto'}}>
							<div className={'text-center'}>
								<div className="d-flex align-items-center mb-2" style={{width: listWidth}}>
									<LanguageShow showLanguages={showLanguages} setShowLanguages={setShowLanguages}/>
									<TableNames/>
								</div>
								<div className="d-flex align-items-center" style={{width: listWidth}}>
									<OperationsManager context={TableContext}/>
									<TableSearch/>
								</div>
								{!loader ? (
									<List
										className="List"
										style={{overflowY: 'scroll'}}
										// 32 è la riga delle label delle dimensioni
										ref={listRef}
										height={Math.min(manualRules.length * 47, 460)}
										innerElementType={innerElementType}
										itemCount={manualRules.length}
										itemSize={47}
										width={listWidth}
									>
										{(props) =>
											DimensionRuleRow({
												...props,
												dimension: detailId,
												linkedDimension: detailLinkedId,
												dimensionType: detailType,
												dimensionReport: detailReport,
												rules: ruleStateManager.rules,
												// Tolgo 1 che è la prima riga degli headers
												rule: manualRules[props.index],
												ruleIndex: props.index,
												removeRule: ruleStateManager.remove,
												otherDimensions: otherDimensionsFiltered,
												dependencyDimensionsTags: dependencyDimensionsTags,
												disabledRules: disabledRules,
												setDisabledRules: setDisabledRules,
												thanosRules: thanosRules,
												setThanosRules: setThanosRules,
												checked: checked,
												setChecked: setChecked,
												showLanguages: showLanguages,
												selectedLanguages: selectedLanguages,
												ratio: ratio
											})
										}
									</List>
								) : <Loading/>}
							</div>
						</div>
					</div>
				</TableContext.Provider>
			</Card>

			<div className={'d-flex justify-content-between ml-4 mr-4 mt-2 mb-2'}>
				<div className={'float-start'}>
					<Button id="regex101" color="link"
									disabled={!(ruleStateManager.rules?.length > 0 && otherDimensions.length > 0) || loader}>
						<a href="https://regex101.com/" target="_blank" rel="noopener noreferrer">Regex101</a>
					</Button>
					<UncontrolledTooltip
						placement="right"
						target="regex101"
					>
						Testa qui la tua regex
					</UncontrolledTooltip>
				</div>

				<div className={'float-end'}>
					{detailType === 'custom-dimensions' && (
						<>
							<ExcelDownload dim={detailId}
														 loader={!(ruleStateManager.rules?.length > 0) || loader}
														 mode={"custom"}/>
							<BulkExcelUpload dim={detailId}
															 loader={!(ruleStateManager.rules?.length > 0) || loader}/>
						</>
					)}
					{/* Helper Regex */}
					<RegexpSeoHelper disabled={loader}/>
					{
						// Mostrato solo nelle dimensioni custom
						detailType === 'custom-dimensions' && (
							<button
								type="button"
								className="btn btn-warning mr-2"
								onClick={(e) => ruleStateManager.create(e, ruleStateManager.rules)}
								disabled={loader}
							>
								Aggiungi Regola
							</button>
						)
					}
					{
						// Mostrato solo nelle dimensioni custom
						(detailType === 'saved-dimensions') && (
							<button
								type="button"
								className="btn btn-warning"
								onClick={(e) => ruleStateManager.create(e, ruleStateManager.rules, 'saved')}
								disabled={loader}
							>
								Aggiungi Regola Salvata
							</button>
						)
					}
				</div>
			</div>
		</>
	)
}
